import { computed, toValue, type ComputedRef, type MaybeRefOrGetter, type Ref } from 'vue'

/**
 * Helper type for query parameters whose availability may be delayed,
 * but we can wait for them before triggering the query.
 */
export type Eventually<T> = MaybeRefOrGetter<T | null | undefined>

/**
 * Returns a computed ref that is true if all the dependencies are available.
 *
 * Useful for checking if all the dependencies are available before fetching data.
 * ⚠️ - Make sure to pass a reactive dependency, otherwise the computed ref will not be reactive.
 *
 * @example
 * ```ts
 * const isReady = isAllAvailable([()=> workspaceId, () => projectId])
 * ```
 */
export function isAllAvailable(
  deps:
    | Record<string, MaybeRefOrGetter<unknown>>
    | MaybeRefOrGetter<unknown>[]
    | MaybeRefOrGetter<unknown>,
): ComputedRef<boolean> {
  return computed(() => {
    if (Array.isArray(deps)) {
      return deps.every((dep) => !!toValue(dep))
    } else if (typeof deps === 'object' && deps !== null) {
      return Object.values(deps).every((dep) => !!toValue(dep))
    }

    return !!toValue(deps)
  })
}

/**
 * In some cases, when retrieving data from a Pinia store, data gets automatically unwrapped.
 * Sometimes TS doesn't know about this, and we may need to fake the type.
 */
export function fakeUnwrap<T>(data: Ref<T>): T {
  return data as unknown as T
}
